/**
 * Artifact Types Panel Component
 * List and manage artifact type configurations
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Loading } from '@/components/common';
import {
  // UI icons
  Plus, AlertCircle, Trash2, Edit2, ChevronDown, ChevronUp, Code, Workflow,
  // Icon picker options - Popular
  Sparkles, Star, Heart, Moon, Sun, Zap, Gift, Award, Crown, Diamond, Gem,
  // Icon picker options - Audio/Media
  Music, Headphones, Mic, Radio, Volume2, Bell, BellRing,
  // Icon picker options - Files
  FileText, File, FileImage, FileAudio, FileVideo, Folder,
  // Icon picker options - Visual
  Image, Camera, Video, Film,
  // Icon picker options - Communication
  MessageCircle, Mail, Send,
  // Icon picker options - People
  User, Users,
  // Icon picker options - Settings
  Settings,
  // Icon picker options - Navigation
  Compass, Map, Globe, Rocket,
  // Icon picker options - Nature
  Cloud, Flame, Droplet,
  // Icon picker options - Tech
  Laptop, Smartphone, Cpu,
  // Icon picker options - Security
  Lock, Key, Shield, Eye,
  // Icon picker options - Time
  Clock, Calendar, Hourglass,
  // Icon picker options - Charts
  TrendingUp, BarChart, PieChart, Activity,
  // Icon picker options - Learning
  BookOpen, Lightbulb, Brain,
  // Icon picker options - Other
  Package, Coffee,
  type LucideIcon
} from 'lucide-react';
import {
  useArtifactTypes,
  useArtifactTypesConfig,
  useSaveArtifactType,
  useDeleteArtifactType,
} from '../hooks/useSystemConfig';
import { useI18n } from '@/i18n';
import type { ArtifactType, ArtifactTypeConfig, ArtifactFieldDef } from '@/types/system';
import { isMediaField, getMediaCategory } from '@/types/system';

// Icon options with visual representation
const ICON_OPTIONS: { id: string; icon: LucideIcon; label: string }[] = [
  // Popular
  { id: 'sparkles', icon: Sparkles, label: 'Sparkles' },
  { id: 'star', icon: Star, label: 'Star' },
  { id: 'heart', icon: Heart, label: 'Heart' },
  { id: 'moon', icon: Moon, label: 'Moon' },
  { id: 'sun', icon: Sun, label: 'Sun' },
  { id: 'zap', icon: Zap, label: 'Zap' },
  { id: 'gem', icon: Gem, label: 'Gem' },
  { id: 'diamond', icon: Diamond, label: 'Diamond' },
  { id: 'crown', icon: Crown, label: 'Crown' },
  { id: 'award', icon: Award, label: 'Award' },
  { id: 'gift', icon: Gift, label: 'Gift' },
  // Audio/Media
  { id: 'headphones', icon: Headphones, label: 'Headphones' },
  { id: 'music', icon: Music, label: 'Music' },
  { id: 'mic', icon: Mic, label: 'Mic' },
  { id: 'radio', icon: Radio, label: 'Radio' },
  { id: 'volume-2', icon: Volume2, label: 'Volume' },
  { id: 'bell', icon: Bell, label: 'Bell' },
  { id: 'bell-ring', icon: BellRing, label: 'Bell Ring' },
  // Files
  { id: 'file-text', icon: FileText, label: 'File Text' },
  { id: 'file', icon: File, label: 'File' },
  { id: 'file-image', icon: FileImage, label: 'File Image' },
  { id: 'file-audio', icon: FileAudio, label: 'File Audio' },
  { id: 'file-video', icon: FileVideo, label: 'File Video' },
  { id: 'folder', icon: Folder, label: 'Folder' },
  // Visual
  { id: 'image', icon: Image, label: 'Image' },
  { id: 'camera', icon: Camera, label: 'Camera' },
  { id: 'video', icon: Video, label: 'Video' },
  { id: 'film', icon: Film, label: 'Film' },
  // Communication
  { id: 'message-circle', icon: MessageCircle, label: 'Message' },
  { id: 'mail', icon: Mail, label: 'Mail' },
  { id: 'send', icon: Send, label: 'Send' },
  // People
  { id: 'user', icon: User, label: 'User' },
  { id: 'users', icon: Users, label: 'Users' },
  // Settings
  { id: 'settings', icon: Settings, label: 'Settings' },
  // Navigation
  { id: 'compass', icon: Compass, label: 'Compass' },
  { id: 'map', icon: Map, label: 'Map' },
  { id: 'globe', icon: Globe, label: 'Globe' },
  { id: 'rocket', icon: Rocket, label: 'Rocket' },
  // Nature
  { id: 'cloud', icon: Cloud, label: 'Cloud' },
  { id: 'flame', icon: Flame, label: 'Flame' },
  { id: 'droplet', icon: Droplet, label: 'Droplet' },
  // Tech
  { id: 'laptop', icon: Laptop, label: 'Laptop' },
  { id: 'smartphone', icon: Smartphone, label: 'Phone' },
  { id: 'cpu', icon: Cpu, label: 'CPU' },
  // Security
  { id: 'lock', icon: Lock, label: 'Lock' },
  { id: 'key', icon: Key, label: 'Key' },
  { id: 'shield', icon: Shield, label: 'Shield' },
  { id: 'eye', icon: Eye, label: 'Eye' },
  // Time
  { id: 'clock', icon: Clock, label: 'Clock' },
  { id: 'calendar', icon: Calendar, label: 'Calendar' },
  { id: 'hourglass', icon: Hourglass, label: 'Hourglass' },
  // Charts
  { id: 'trending-up', icon: TrendingUp, label: 'Trending' },
  { id: 'bar-chart', icon: BarChart, label: 'Bar Chart' },
  { id: 'pie-chart', icon: PieChart, label: 'Pie Chart' },
  { id: 'activity', icon: Activity, label: 'Activity' },
  // Learning
  { id: 'book-open', icon: BookOpen, label: 'Book' },
  { id: 'lightbulb', icon: Lightbulb, label: 'Lightbulb' },
  { id: 'brain', icon: Brain, label: 'Brain' },
  // Other
  { id: 'package', icon: Package, label: 'Package' },
  { id: 'coffee', icon: Coffee, label: 'Coffee' },
];

// Icon Picker Component
interface IconPickerProps {
  value: string;
  onChange: (iconId: string) => void;
}

const IconPicker: React.FC<IconPickerProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Find current icon
  const currentIcon = ICON_OPTIONS.find((opt) => opt.id === value);
  const CurrentIconComponent = currentIcon?.icon || Package;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 bg-white text-left flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <CurrentIconComponent className="w-5 h-5 text-gray-600" />
          <span className="text-sm text-gray-600">{currentIcon?.label || value}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 grid grid-cols-8 gap-1 max-h-64 overflow-y-auto">
            {ICON_OPTIONS.map((option) => {
              const IconComponent = option.icon;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    onChange(option.id);
                    setIsOpen(false);
                  }}
                  title={option.label}
                  className={`p-2 hover:bg-gray-100 rounded flex items-center justify-center ${
                    value === option.id ? 'bg-blue-100 ring-2 ring-blue-500' : ''
                  }`}
                >
                  <IconComponent className="w-5 h-5 text-gray-600" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Get icon component from ICON_OPTIONS
const getIconComponent = (iconId?: string): LucideIcon | null => {
  if (!iconId) return null;
  const iconOption = ICON_OPTIONS.find((opt) => opt.id === iconId);
  return iconOption?.icon || null;
};

// Rotation badge color
const getRotationBadge = (rotation?: string | null) => {
  if (!rotation) return null;
  const colors: Record<string, string> = {
    daily: 'bg-green-100 text-green-800',
    weekly: 'bg-blue-100 text-blue-800',
    monthly: 'bg-purple-100 text-purple-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[rotation] || 'bg-gray-100 text-gray-800'}`}>
      {rotation}
    </span>
  );
};

interface ArtifactTypeCardProps {
  artifactType: ArtifactType;
  onEdit: (type: ArtifactType) => void;
  onDelete: (typeId: string) => void;
  isEditing?: boolean;
}

const ArtifactTypeCard: React.FC<ArtifactTypeCardProps> = ({ artifactType, onEdit, onDelete, isEditing }) => {
  const { messages } = useI18n();
  const t = messages.systemSettings.artifactTypes;
  const iconValue = artifactType.config.icon;
  const IconComponent = getIconComponent(iconValue) || Package;

  return (
    <Card className={`p-4 transition-colors ${isEditing ? 'ring-2 ring-blue-500 bg-blue-50/30' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-gray-100 rounded-lg flex items-center justify-center w-10 h-10">
            <IconComponent className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-gray-900">{artifactType.config.name}</h3>
              {artifactType.config.name_zh && (
                <span className="text-sm text-gray-500">({artifactType.config.name_zh})</span>
              )}
              {getRotationBadge(artifactType.config.rotation)}
            </div>
            <p className="text-sm text-gray-500 mt-1">ID: {artifactType.id}</p>
            {artifactType.config.description && (
              <p className="text-sm text-gray-600 mt-1">{artifactType.config.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              {artifactType.config.max_stored && (
                <span>Max: {artifactType.config.max_stored}</span>
              )}
              {artifactType.config.data_schema && artifactType.config.data_schema.length > 0 && (
                <span className="text-blue-600">
                  {artifactType.config.data_schema.length} {t?.fields || 'fields'}
                  {(() => {
                    const mediaFields = artifactType.config.data_schema.filter(f => isMediaField(f));
                    const metadataFields = artifactType.config.data_schema.filter(f => !isMediaField(f));
                    const parts = [];
                    if (mediaFields.length > 0) parts.push(`${mediaFields.length} ${t?.mediaFields || 'media'}`);
                    if (metadataFields.length > 0) parts.push(`${metadataFields.length} ${t?.metadataFields || 'metadata'}`);
                    return parts.length > 0 ? ` (${parts.join(', ')})` : '';
                  })()}
                </span>
              )}
            </div>
            <div className="mt-2 space-y-0.5 text-xs text-gray-500">
              <div>
                {t?.lifecycleMode || '生命周期模式'}: <span className="font-mono text-gray-700">{artifactType.config.lifecycle_mode ?? 'rotation'}</span>
                {!artifactType.config.lifecycle_mode && <span className="text-gray-400"> ({t?.defaultSuffix || '默认'})</span>}
                <span className="mx-2">·</span>
                {t?.sortBy || '排序字段'}: <span className="font-mono text-gray-700">{artifactType.config.sort_by ?? 'created_at'}</span>
                {!artifactType.config.sort_by && <span className="text-gray-400"> ({t?.defaultSuffix || '默认'})</span>}
              </div>
              {artifactType.config.lifecycle_mode === 'scheduled' && (
                <div>
                  {t?.retentionDays || '保留天数 (valid_from 之后)'}: <span className="font-mono text-gray-700">{artifactType.config.retention_days_after_valid ?? '未设置'}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <span className="text-xs text-blue-600 font-medium px-2">{t?.editing || 'Editing...'}</span>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => onEdit(artifactType)}>
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const confirmMsg = (t?.confirmDelete || 'Are you sure you want to delete artifact type "{id}"?').replace('{id}', artifactType.id);
                  if (confirm(confirmMsg)) {
                    onDelete(artifactType.id);
                  }
                }}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

interface ArtifactTypeFormProps {
  type?: ArtifactType;
  onSave: (typeId: string, config: ArtifactTypeConfig, isNew: boolean) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

// Content types grouped by category for field-level selection
const FIELD_CONTENT_TYPE_OPTIONS: { value: string; label: string; category: string }[] = [
  // Images
  { value: 'image/png', label: 'PNG', category: '图片' },
  { value: 'image/jpeg', label: 'JPEG', category: '图片' },
  { value: 'image/webp', label: 'WebP', category: '图片' },
  { value: 'image/gif', label: 'GIF', category: '图片' },
  { value: 'image/svg+xml', label: 'SVG', category: '图片' },
  // Audio
  { value: 'audio/mpeg', label: 'MP3', category: '音频' },
  { value: 'audio/wav', label: 'WAV', category: '音频' },
  { value: 'audio/mp4', label: 'M4A', category: '音频' },
  { value: 'audio/ogg', label: 'OGG', category: '音频' },
  // Video
  { value: 'video/mp4', label: 'MP4', category: '视频' },
  { value: 'video/webm', label: 'WebM', category: '视频' },
  // Documents
  { value: 'text/markdown', label: 'Markdown', category: '文档' },
  { value: 'text/plain', label: 'Text', category: '文档' },
  { value: 'application/pdf', label: 'PDF', category: '文档' },
  { value: 'application/json', label: 'JSON', category: '文档' },
];

// Default empty field for data schema
const createEmptyField = (): ArtifactFieldDef => ({
  key: '',
  label: '',
  label_zh: '',
  required: true,
  content_types: undefined, // No content_types = metadata field
});

const ArtifactTypeForm: React.FC<ArtifactTypeFormProps> = ({ type, onSave, onCancel, isLoading }) => {
  const { messages } = useI18n();
  const t = messages.systemSettings.artifactTypes;
  const isNew = !type;
  const [typeId, setTypeId] = useState(type?.id || '');
  const [name, setName] = useState(type?.config.name || '');
  const [nameZh, setNameZh] = useState(type?.config.name_zh || '');
  const [description, setDescription] = useState(type?.config.description || '');
  const [rotation, setRotation] = useState<string>(type?.config.rotation || '');
  const [maxStored, setMaxStored] = useState(type?.config.max_stored?.toString() || '');
  const [icon, setIcon] = useState(type?.config.icon || 'package');
  const [dataSchema, setDataSchema] = useState<ArtifactFieldDef[]>(type?.config.data_schema || []);

  // Data schema field handlers
  const addSchemaField = () => {
    setDataSchema([...dataSchema, createEmptyField()]);
  };

  const removeSchemaField = (index: number) => {
    setDataSchema(dataSchema.filter((_, i) => i !== index));
  };

  const updateSchemaField = (index: number, field: Partial<ArtifactFieldDef>) => {
    setDataSchema(dataSchema.map((f, i) => (i === index ? { ...f, ...field } : f)));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate: must have at least one field
    const validSchema = dataSchema.filter(f => f.key.trim() !== '');
    if (validSchema.length === 0) {
      alert('请至少定义一个数据字段');
      return;
    }

    const config: ArtifactTypeConfig = {
      name,
      name_zh: nameZh || undefined,
      description: description || undefined,
      rotation: rotation ? (rotation as 'daily' | 'weekly' | 'monthly') : null,
      max_stored: maxStored ? parseInt(maxStored, 10) : null,
      icon: icon || undefined,
      auto_cleanup: false,
      data_schema: validSchema,
    };

    onSave(typeId, config, isNew);
  };

  return (
    <Card className="p-6">
      <h3 className="text-lg font-medium mb-4">{type ? t?.editType : t?.addTypeTitle}</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t?.typeId}</label>
            <Input
              value={typeId}
              onChange={(e) => setTypeId(e.target.value)}
              placeholder="e.g., lucky_charm"
              disabled={!!type}
              required
              pattern="^[a-z][a-z0-9_]*$"
              title={t?.typeIdHint}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t?.icon}</label>
            <IconPicker value={icon} onChange={setIcon} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t?.nameEnglish}</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Lucky Charm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t?.nameChinese}</label>
            <Input
              value={nameZh}
              onChange={(e) => setNameZh(e.target.value)}
              placeholder="e.g., 幸运符"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t?.descriptionLabel}</label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t?.descriptionHint}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t?.rotationPeriod}</label>
            <select
              value={rotation}
              onChange={(e) => setRotation(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t?.rotationOptions?.none}</option>
              <option value="daily">{t?.rotationOptions?.daily}</option>
              <option value="weekly">{t?.rotationOptions?.weekly}</option>
              <option value="monthly">{t?.rotationOptions?.monthly}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t?.maxStored}</label>
            <Input
              type="number"
              min="1"
              value={maxStored}
              onChange={(e) => setMaxStored(e.target.value)}
              placeholder={t?.maxStoredHint}
            />
          </div>
        </div>

        {/* Data Schema Section */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">数据字段 (Data Schema) <span className="text-red-500">*</span></label>
              <p className="text-xs text-gray-500 mt-0.5">
                定义产物的数据结构。不指定内容类型 = <span className="text-gray-700">元数据</span>字段；
                指定内容类型 = <span className="text-purple-600">媒体</span>字段（存储文件路径）
              </p>
            </div>
            <Button type="button" variant="secondary" size="sm" onClick={addSchemaField}>
              <Plus className="w-3 h-3 mr-1" />
              添加字段
            </Button>
          </div>

          {dataSchema.length === 0 ? (
            <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
              <p className="text-sm text-gray-500">请至少定义一个数据字段</p>
              <p className="text-xs text-gray-400 mt-1">点击"添加字段"来定义产物需要的数据结构</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dataSchema.map((field, index) => {
                const isMedia = isMediaField(field);
                const mediaCategory = field.content_types ? getMediaCategory(field.content_types) : null;

                return (
                  <div key={index} className={`p-3 rounded-lg border ${isMedia ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
                    {/* Row 1: Key, Labels, Required, Delete */}
                    <div className="flex items-start gap-3">
                      {/* Field Key */}
                      <div className="w-32">
                        <label className="block text-xs font-medium text-gray-600 mb-1">字段Key <span className="text-red-500">*</span></label>
                        <Input
                          value={field.key}
                          onChange={(e) => updateSchemaField(index, { key: e.target.value })}
                          placeholder="image"
                          className="text-sm"
                          required
                        />
                      </div>

                      {/* Label EN */}
                      <div className="flex-1 min-w-0">
                        <label className="block text-xs font-medium text-gray-600 mb-1">英文标签</label>
                        <Input
                          value={field.label || ''}
                          onChange={(e) => updateSchemaField(index, { label: e.target.value })}
                          placeholder="Image"
                          className="text-sm"
                        />
                      </div>

                      {/* Label ZH */}
                      <div className="flex-1 min-w-0">
                        <label className="block text-xs font-medium text-gray-600 mb-1">中文标签</label>
                        <Input
                          value={field.label_zh || ''}
                          onChange={(e) => updateSchemaField(index, { label_zh: e.target.value })}
                          placeholder="图片"
                          className="text-sm"
                        />
                      </div>

                      {/* Required Checkbox */}
                      <div className="w-16 pt-5">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={field.required || false}
                            onChange={(e) => updateSchemaField(index, { required: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-600">必填</span>
                        </label>
                      </div>

                      {/* Delete Button */}
                      <div className="pt-5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSchemaField(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Row 2: Content Types Selection */}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        内容类型
                        <span className="font-normal text-gray-400 ml-2">
                          {isMedia ? (
                            <span className="text-purple-600">媒体字段 ({mediaCategory})</span>
                          ) : (
                            '元数据字段（不选则为纯文本/JSON值）'
                          )}
                        </span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {/* Group by category */}
                        {['图片', '音频', '视频', '文档'].map(category => {
                          const categoryTypes = FIELD_CONTENT_TYPE_OPTIONS.filter(o => o.category === category);
                          const selectedInCategory = categoryTypes.filter(o => field.content_types?.includes(o.value));

                          return (
                            <div key={category} className="flex items-center gap-1">
                              <span className="text-xs text-gray-500">{category}:</span>
                              {categoryTypes.map(opt => {
                                const isSelected = field.content_types?.includes(opt.value);
                                return (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => {
                                      const current = field.content_types || [];
                                      if (isSelected) {
                                        updateSchemaField(index, {
                                          content_types: current.filter(t => t !== opt.value).length > 0
                                            ? current.filter(t => t !== opt.value)
                                            : undefined
                                        });
                                      } else {
                                        // Only allow same category types
                                        const newTypes = selectedInCategory.length > 0
                                          ? [...current, opt.value]
                                          : [opt.value]; // Start fresh if switching categories
                                        updateSchemaField(index, { content_types: newTypes });
                                      }
                                    }}
                                    className={`px-2 py-0.5 text-xs rounded ${
                                      isSelected
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-white border border-gray-300 text-gray-600 hover:border-purple-300'
                                    }`}
                                  >
                                    {opt.label}
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            {t?.cancel}
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? t?.saving : t?.save}
          </Button>
        </div>
      </form>
    </Card>
  );
};

// Documentation Section Component
const ArtifactDocumentation: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const CodeBlock: React.FC<{ children: string; language?: string }> = ({ children }) => (
    <pre className="bg-gray-900 text-gray-100 p-3 rounded-md overflow-x-auto text-xs font-mono">
      {children}
    </pre>
  );

  return (
    <div className="space-y-3">
      {/* Overview */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
        <p className="font-medium mb-2">About Artifact Types (Multi-Field Architecture)</p>
        <ul className="space-y-1 list-disc list-inside">
          <li>Each artifact is a <strong>folder</strong> containing metadata.json + media files</li>
          <li>Data schema defines fields: <span className="text-gray-700">metadata</span> (stored in JSON) or <span className="text-purple-600">media</span> (stored as files)</li>
          <li>Version format: YYYYMMDD (daily), YYYYWww (weekly), YYYYMM (monthly), YYYYMMDD_HHmmss (non-periodic)</li>
          <li>Rotation period determines when artifacts expire and new ones become "current"</li>
          <li>Max stored limits how many artifacts to keep per user (oldest are marked expired)</li>
        </ul>
      </div>

      {/* Storage Structure */}
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm">
        <p className="font-medium mb-2 text-gray-800">Storage Structure</p>
        <CodeBlock>{`artifacts/{user_id}/{artifact_type}/{version}/
├── metadata.json              # All field values
├── {field_key}.{ext}          # Media files (e.g., image.png, audio.mp3)
└── ...

# metadata.json example:
{
  "image": "image.png",        # Media field: filename reference
  "phrase": "Lucky phrase",    # Metadata field: direct value
  "description": "Based on..."
}`}</CodeBlock>
      </div>

      {/* API Access Section */}
      <Card className="overflow-hidden">
        <button
          className="w-full p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={() => toggleSection('api')}
        >
          <div className="flex items-center gap-2">
            <Code className="w-5 h-5 text-green-600" />
            <span className="font-medium">API Access (App Integration)</span>
          </div>
          {expandedSection === 'api' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        {expandedSection === 'api' && (
          <div className="p-4 space-y-4 text-sm">
            <p className="text-gray-600">
              Use these endpoints in your app to fetch user artifacts. All endpoints require authentication.
            </p>

            {/* Get Current Artifact */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">GET</span>
                <code className="text-sm font-mono">/v1/artifacts/{'{type}'}/current</code>
                <span className="text-gray-500 text-xs">Primary endpoint</span>
              </div>
              <p className="text-gray-600 text-xs">
                Get the current (latest non-expired) artifact with all field values and presigned URLs.
              </p>
              <CodeBlock>{`// Example: Get today's lucky charm
const response = await fetch('/v1/artifacts/lucky_charm/current', {
  headers: { 'Authorization': 'Bearer <token>' }
});
const artifact = await response.json();

// Response (multi-field artifact)
{
  "id": "uuid",
  "artifact_type": "lucky_charm",
  "version": "20251231",
  "title": "Today's Lucky Charm",
  "is_current": true,
  "is_expired": false,
  "field_files": {"image": "image.png"},
  "fields": {
    "image": {
      "key": "image",
      "value": "image.png",
      "is_media": true,
      "url": "https://storage.../presigned-url",
      "content_type": "image/png"
    },
    "phrase": {
      "key": "phrase",
      "value": "Today is your lucky day!",
      "is_media": false
    }
  }
}`}</CodeBlock>
            </div>

            {/* Get Field Directly */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">GET</span>
                <code className="text-sm font-mono">/v1/artifacts/{'{type}'}/current/{'{field_key}'}</code>
                <span className="text-gray-500 text-xs">Direct field access</span>
              </div>
              <p className="text-gray-600 text-xs">
                Get a specific field from current artifact. Returns 302 redirect to presigned URL for media fields.
              </p>
              <CodeBlock>{`// Get image field directly (returns 302 redirect)
GET /v1/artifacts/lucky_charm/current/image
→ 302 Redirect to presigned URL

// Use in <img> tag directly
<img src="/v1/artifacts/lucky_charm/current/image" />`}</CodeBlock>
            </div>

            {/* List Versions */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">GET</span>
                <code className="text-sm font-mono">/v1/artifacts/{'{type}'}/versions</code>
              </div>
              <p className="text-gray-600 text-xs">
                List available versions for an artifact type.
              </p>
              <CodeBlock>{`// List versions
GET /v1/artifacts/lucky_charm/versions

// Response
{
  "versions": [
    {"version": "20251231", "is_current": true, "is_expired": false},
    {"version": "20251230", "is_current": false, "is_expired": false},
    {"version": "20251229", "is_current": false, "is_expired": true}
  ],
  "current": "20251231"
}`}</CodeBlock>
            </div>

            {/* Get By Version */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">GET</span>
                <code className="text-sm font-mono">/v1/artifacts/{'{type}'}/{'{version}'}</code>
              </div>
              <p className="text-gray-600 text-xs">
                Get a specific version of an artifact.
              </p>
              <CodeBlock>{`// Get yesterday's lucky charm
GET /v1/artifacts/lucky_charm/20251230

// Get specific field from version
GET /v1/artifacts/lucky_charm/20251230/image`}</CodeBlock>
            </div>

            {/* List Types */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">GET</span>
                <code className="text-sm font-mono">/v1/artifacts/types</code>
              </div>
              <p className="text-gray-600 text-xs">
                List all available artifact types with their data schemas.
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* Workflow Integration Section */}
      <Card className="overflow-hidden">
        <button
          className="w-full p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={() => toggleSection('workflow')}
        >
          <div className="flex items-center gap-2">
            <Workflow className="w-5 h-5 text-purple-600" />
            <span className="font-medium">Creating Artifacts (Service API)</span>
          </div>
          {expandedSection === 'workflow' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        {expandedSection === 'workflow' && (
          <div className="p-4 space-y-4 text-sm">
            <p className="text-gray-600">
              Artifacts are created through the UserArtifactService. Each artifact is a folder with multiple fields.
            </p>

            {/* Create Artifact Example */}
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">Creating Multi-Field Artifacts</h4>
              <p className="text-gray-600 text-xs">
                Use the service to create artifacts with multiple fields (media + metadata):
              </p>
              <CodeBlock>{`# Python Service Example
from api.services.artifacts import UserArtifactService
from api.services.oss.artifact_storage import ArtifactFieldInput

service = UserArtifactService(db)

# Create artifact with multiple fields
artifact = await service.create_artifact(
    user_id="user_123",
    artifact_type="lucky_charm",
    fields={
        # Media field: copy from temp storage
        "image": ArtifactFieldInput(
            type="temp_copy",
            temp_object_name="temp/abc123.png",
            filename="image.png",
            content_type="image/png"
        ),
        # Metadata field: direct value
        "phrase": ArtifactFieldInput(
            type="value",
            value="Today is your lucky day!"
        ),
        "description": ArtifactFieldInput(
            type="value",
            value="Based on your natal chart..."
        )
    },
    title="Lucky Charm for 2025-12-31"
)`}</CodeBlock>
            </div>

            {/* Field Input Types */}
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">ArtifactFieldInput Types</h4>
              <div className="text-xs text-gray-600 space-y-2">
                <div className="bg-gray-50 p-2 rounded">
                  <span className="font-mono text-purple-600">temp_copy</span>: Copy file from temp storage to artifact folder
                </div>
                <div className="bg-gray-50 p-2 rounded">
                  <span className="font-mono text-purple-600">upload</span>: Upload raw bytes directly
                </div>
                <div className="bg-gray-50 p-2 rounded">
                  <span className="font-mono text-gray-700">value</span>: Store metadata value in metadata.json
                </div>
              </div>
            </div>

            {/* Rotation Behavior */}
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">Rotation & Versioning</h4>
              <div className="text-xs text-gray-600 space-y-1">
                <p><strong>Daily (YYYYMMDD):</strong> Version "20251231". Valid until end of day.</p>
                <p><strong>Weekly (YYYYWww):</strong> Version "2025W52". Valid until end of week.</p>
                <p><strong>Monthly (YYYYMM):</strong> Version "202512". Valid until end of month.</p>
                <p><strong>None (YYYYMMDD_HHmmss):</strong> Version "20251231_143052". Permanent artifacts.</p>
              </div>
            </div>

            {/* Max Stored */}
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">Max Stored Limit</h4>
              <p className="text-gray-600 text-xs">
                When max_stored is set (e.g., 7), after creating a new artifact:
              </p>
              <ol className="list-decimal list-inside text-xs text-gray-600 space-y-1">
                <li>New artifact folder is created with version-based path</li>
                <li>Previous "current" artifacts are marked as not current</li>
                <li>If total exceeds max_stored, oldest are marked as expired</li>
                <li>Expired artifact folders can be cleaned up via Admin API</li>
              </ol>
            </div>
          </div>
        )}
      </Card>

      {/* Field Reference Section */}
      <Card className="overflow-hidden">
        <button
          className="w-full p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={() => toggleSection('fields')}
        >
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-orange-600" />
            <span className="font-medium">Configuration Reference</span>
          </div>
          {expandedSection === 'fields' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        {expandedSection === 'fields' && (
          <div className="p-4 text-sm space-y-4">
            {/* Type-level fields */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Artifact Type Configuration</h4>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 font-medium text-gray-900">Field</th>
                    <th className="text-left py-2 pr-4 font-medium text-gray-900">Required</th>
                    <th className="text-left py-2 font-medium text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600">
                  <tr className="border-b">
                    <td className="py-2 pr-4 font-mono">type_id</td>
                    <td className="py-2 pr-4">Yes</td>
                    <td className="py-2">Unique identifier (snake_case, e.g., lucky_charm)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4 font-mono">name / name_zh</td>
                    <td className="py-2 pr-4">Yes / No</td>
                    <td className="py-2">Display names (English required, Chinese optional)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4 font-mono">data_schema</td>
                    <td className="py-2 pr-4">Yes</td>
                    <td className="py-2">Array of field definitions (see below)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4 font-mono">rotation</td>
                    <td className="py-2 pr-4">No</td>
                    <td className="py-2">Rotation period: daily, weekly, monthly, or null</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4 font-mono">max_stored</td>
                    <td className="py-2 pr-4">No</td>
                    <td className="py-2">Max artifacts per user (null = unlimited)</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-mono">icon</td>
                    <td className="py-2 pr-4">No</td>
                    <td className="py-2">Icon identifier for display</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Data Schema fields */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Data Schema Field Definition</h4>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 font-medium text-gray-900">Field</th>
                    <th className="text-left py-2 pr-4 font-medium text-gray-900">Required</th>
                    <th className="text-left py-2 font-medium text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600">
                  <tr className="border-b">
                    <td className="py-2 pr-4 font-mono">key</td>
                    <td className="py-2 pr-4">Yes</td>
                    <td className="py-2">Field identifier (e.g., "image", "phrase")</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4 font-mono">label / label_zh</td>
                    <td className="py-2 pr-4">No</td>
                    <td className="py-2">Display labels for the field</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4 font-mono">required</td>
                    <td className="py-2 pr-4">No</td>
                    <td className="py-2">Whether field is required (default: false)</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-mono">content_types</td>
                    <td className="py-2 pr-4">No</td>
                    <td className="py-2">
                      <span className="text-purple-600">Array = media field</span> (file stored separately)<br/>
                      <span className="text-gray-500">Omit = metadata field</span> (value in metadata.json)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Field Type Examples */}
            <div className="bg-gray-50 p-3 rounded text-xs">
              <p className="font-medium text-gray-700 mb-2">Example data_schema:</p>
              <CodeBlock>{`"data_schema": [
  // Media field: has content_types, stored as file
  {"key": "image", "label": "Image", "required": true,
   "content_types": ["image/png", "image/jpeg"]},

  // Metadata field: no content_types, stored in JSON
  {"key": "phrase", "label": "Phrase", "required": true},
  {"key": "description", "label": "Description", "required": false}
]`}</CodeBlock>
            </div>
          </div>
        )}
      </Card>

      {/* Admin Operations Section */}
      <Card className="overflow-hidden">
        <button
          className="w-full p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={() => toggleSection('admin')}
        >
          <div className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" />
            <span className="font-medium">Admin Operations (Cleanup & Management)</span>
          </div>
          {expandedSection === 'admin' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        {expandedSection === 'admin' && (
          <div className="p-4 space-y-4 text-sm">
            <p className="text-gray-600">
              Admin endpoints for managing artifacts across all users.
            </p>

            {/* Resource Stats & Management */}
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">Resource Management (Unified API)</h4>
              <div className="space-y-3 text-xs">
                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded font-medium">GET</span>
                  <div>
                    <code className="font-mono">/admin/resources/stats</code>
                    <p className="text-gray-500 mt-1">Get system-wide resource statistics (artifacts, library, temp)</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded font-medium">GET</span>
                  <div>
                    <code className="font-mono">/admin/resources/user/{'{user_id}'}/summary</code>
                    <p className="text-gray-500 mt-1">Get resource summary for a specific user</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded font-medium">POST</span>
                  <div>
                    <code className="font-mono">/admin/resources/cleanup/expired</code>
                    <p className="text-gray-500 mt-1">Cleanup expired artifacts (mark expired, optionally delete files)</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded font-medium">POST</span>
                  <div>
                    <code className="font-mono">/admin/resources/cleanup/temp</code>
                    <p className="text-gray-500 mt-1">Cleanup old temp files (configurable days threshold)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* System Tasks - Scheduled Cleanup */}
            <div className="space-y-2 pt-2 border-t">
              <h4 className="font-medium text-gray-900">Scheduled Cleanup (System Tasks)</h4>
              <p className="text-gray-600 text-xs">
                Use system tasks for background/scheduled cleanup operations.
              </p>
              <div className="space-y-3 text-xs">
                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded font-medium">GET</span>
                  <div>
                    <code className="font-mono">/admin/system-tasks/artifact-cleanup/preview</code>
                    <p className="text-gray-500 mt-1">Preview what cleanup would affect (counts, storage size)</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded font-medium">POST</span>
                  <div>
                    <code className="font-mono">/admin/system-tasks/artifact-cleanup</code>
                    <p className="text-gray-500 mt-1">Run cleanup immediately (synchronous)</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded font-medium">POST</span>
                  <div>
                    <code className="font-mono">/admin/system-tasks/artifact-cleanup/schedule</code>
                    <p className="text-gray-500 mt-1">Queue cleanup for background execution</p>
                  </div>
                </div>
              </div>

              <CodeBlock>{`// Schedule background cleanup
POST /admin/system-tasks/artifact-cleanup/schedule
{
  "artifact_type": "lucky_charm",  // optional filter
  "delete_files": true,            // delete artifact folders from storage
  "delete_records": false          // keep DB records
}

// Response
{
  "status": "scheduled",
  "task_type": "artifact_cleanup",
  "message": "Cleanup task has been queued"
}

// Note: delete_files=true removes the entire artifact folder:
// artifacts/{user_id}/{type}/{version}/ (metadata.json + all media files)`}</CodeBlock>
            </div>

            {/* Cron-based Scheduled Cleanup */}
            <div className="space-y-2 pt-2 border-t">
              <h4 className="font-medium text-gray-900">Cron-based Scheduled Cleanup (Recommended)</h4>
              <p className="text-gray-600 text-xs">
                For automated recurring cleanup, use System Task Schedules with cron expressions:
              </p>
              <div className="space-y-3 text-xs">
                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded font-medium">GET</span>
                  <div>
                    <code className="font-mono">/admin/system-task-schedules</code>
                    <p className="text-gray-500 mt-1">List all system task schedules</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded font-medium">POST</span>
                  <div>
                    <code className="font-mono">/admin/system-task-schedules</code>
                    <p className="text-gray-500 mt-1">Create a new scheduled system task</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded font-medium">POST</span>
                  <div>
                    <code className="font-mono">/admin/system-task-schedules/{'{id}'}/toggle</code>
                    <p className="text-gray-500 mt-1">Enable/disable a schedule</p>
                  </div>
                </div>
              </div>

              <CodeBlock>{`// Create a daily cleanup schedule (runs at 3 AM UTC)
POST /admin/system-task-schedules
{
  "task_type": "artifact_cleanup",
  "name": "Daily Artifact Cleanup",
  "description": "Clean up expired artifacts every day",
  "schedule_type": "cron",
  "schedule_config": {
    "expression": "0 3 * * *",  // 3 AM UTC daily
    "timezone": "UTC"
  },
  "task_config": {
    "delete_files": true,
    "delete_records": false
  },
  "is_enabled": true
}

// Common cron expressions:
// "0 3 * * *"    - Daily at 3 AM
// "0 3 * * 0"    - Weekly on Sunday at 3 AM
// "0 3 1 * *"    - Monthly on 1st at 3 AM`}</CodeBlock>

              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800">
                <strong>Note:</strong> A default "Daily Artifact Cleanup" schedule is created by migration but <strong>disabled by default</strong>.
                Enable it via the API or create your own schedule with custom settings.
              </div>
            </div>

            {/* Worker Setup */}
            <div className="space-y-2 pt-2 border-t">
              <h4 className="font-medium text-gray-900">Required Services</h4>
              <p className="text-gray-600 text-xs">
                For scheduled cleanup to work, ensure these services are running:
              </p>
              <CodeBlock>{`# 1. Scheduler Service (APScheduler-based, loads schedules from DB)
cd scheduler-service && python main.py

# 2. System Task Worker (processes queued tasks from Redis)
python -m api.workers.system_task_worker

# Both services use Redis for communication`}</CodeBlock>
            </div>

            <div className="bg-green-50 border border-green-200 rounded p-3 text-xs text-green-800">
              <strong>Tip:</strong> Use cron-based schedules for automated recurring cleanup. Use <code>/schedule</code> endpoints for one-time background execution.
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export const ArtifactTypesPanel: React.FC = () => {
  const { messages } = useI18n();
  const t = messages.systemSettings.artifactTypes;
  const [editingType, setEditingType] = useState<ArtifactType | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const { data: types, isLoading, error } = useArtifactTypes();
  const { data: config } = useArtifactTypesConfig();
  const saveType = useSaveArtifactType();
  const deleteType = useDeleteArtifactType();

  const handleSave = async (typeId: string, typeConfig: ArtifactTypeConfig, isNew: boolean) => {
    try {
      await saveType.mutateAsync({ typeId, config: typeConfig, isNew });
      setEditingType(null);
      setIsAdding(false);
    } catch (error) {
      alert(`${t?.saveFailed}: ${error}`);
    }
  };

  const handleDelete = async (typeId: string) => {
    try {
      await deleteType.mutateAsync(typeId);
    } catch (error) {
      alert(`${t?.deleteFailed}: ${error}`);
    }
  };

  if (isLoading) {
    return <Loading message={t?.loading} />;
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>{t?.loadFailed}: {error.message}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{t?.title}</h2>
          <p className="text-sm text-gray-600 mt-1">
            {t?.description}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              setEditingType(null);
              setIsAdding(true);
            }}
            disabled={isAdding}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t?.addType}
          </Button>
        </div>
      </div>

      {/* Add new form - renders at the top */}
      {isAdding && (
        <ArtifactTypeForm
          key="new"
          onSave={handleSave}
          onCancel={() => setIsAdding(false)}
          isLoading={saveType.isPending}
        />
      )}

      {/* Type List with inline edit form */}
      {types && types.length > 0 ? (
        <div className="space-y-3">
          {types.map((type) => (
            <React.Fragment key={type.id}>
              <ArtifactTypeCard
                artifactType={type}
                onEdit={(t) => {
                  setIsAdding(false);
                  setEditingType(t);
                }}
                onDelete={handleDelete}
                isEditing={editingType?.id === type.id}
              />
              {/* Inline edit form - renders below the card being edited */}
              {editingType?.id === type.id && (
                <ArtifactTypeForm
                  key={`edit-${type.id}`}
                  type={editingType}
                  onSave={handleSave}
                  onCancel={() => setEditingType(null)}
                  isLoading={saveType.isPending}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      ) : !isAdding ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-600">{t?.noTypes}</p>
          <button
            onClick={() => setIsAdding(true)}
            className="mt-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            {t?.addFirstType}
          </button>
        </div>
      ) : null}

      {/* Documentation Section */}
      <ArtifactDocumentation />

      {/* Metadata */}
      {config?.version !== undefined && (
        <div className="text-sm text-gray-500">
          <p>
            {t?.version}: <span>{config.version}</span> |
            {t?.lastUpdatedBy}: <span>{config.updated_by || 'N/A'}</span> |
            <span> {config.updated_at ? new Date(config.updated_at).toLocaleString() : 'N/A'}</span>
          </p>
        </div>
      )}
    </div>
  );
};
